import { SURVEY_OPTION_NONE_ID, SURVEY_OPTION_OTHER_ID } from './survey-option-ids'
import type { SurveyQuestion } from './survey-question.types'

export const CHOICE_TEXT_RESPONSE_MESSAGE = 'Please enter a response before continuing.'
export const CHOICE_TEXT_RESPONSE_LABEL = 'Please specify'
export const CHOICE_TEXT_RESPONSE_PLACEHOLDER = 'Type your answer here....'

type ChoiceTextQuestion = Pick<
  SurveyQuestion,
  'type' | 'hasOtherOption' | 'hasNoneOfAboveOption' | 'textResponse'
>

const CHOICE_TYPES = new Set<SurveyQuestion['type']>(['multiple_choice', 'checkbox', 'dropdown'])

/**
 * BFP-1156 — option id whose selection reveals the specify field, or `null`.
 * Legacy questions (no `textResponse` key) keep the always-on Other field.
 */
export function choiceTextTargetId(question: ChoiceTextQuestion): string | null {
  if (!CHOICE_TYPES.has(question.type)) return null
  const tr = question.textResponse
  if (tr === undefined) return question.hasOtherOption ? SURVEY_OPTION_OTHER_ID : null
  if (tr == null) return null
  if (tr.target === 'other') return question.hasOtherOption ? SURVEY_OPTION_OTHER_ID : null
  if (tr.target === 'none') return question.hasNoneOfAboveOption ? SURVEY_OPTION_NONE_ID : null
  return tr.optionId ?? null
}

export function isChoiceTextTargetSelected(
  question: ChoiceTextQuestion,
  selectedIds: readonly string[],
): boolean {
  const target = choiceTextTargetId(question)
  return target != null && selectedIds.includes(target)
}

export function choiceTextMaxCharacters(question: ChoiceTextQuestion): number | null {
  return question.textResponse?.maxCharacters ?? null
}

/** Trimmed text must be non-empty and inside the configured min/max. */
export function isChoiceTextWithinLimits(question: ChoiceTextQuestion, text: string): boolean {
  const len = text.trim().length
  if (len === 0) return false
  const min = question.textResponse?.minCharacters ?? null
  const max = question.textResponse?.maxCharacters ?? null
  if (min != null && len < min) return false
  if (max != null && len > max) return false
  return true
}

/** True when the text-enabled option is selected but its text is missing or out of range. */
export function isChoiceTextResponseInvalid(
  question: ChoiceTextQuestion,
  selectedIds: readonly string[],
  text: string,
): boolean {
  if (!isChoiceTextTargetSelected(question, selectedIds)) return false
  return !isChoiceTextWithinLimits(question, text)
}
